/** Shared week/month/day period options for `/review` and `/review/:podcastId` (Chilean time, DST-aware). */

import { enUS } from "date-fns/locale";

export type ReviewDateFilterMode = 'week' | 'month' | 'day';

const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function formatYmdFromChileanDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function formatDayRangeValue(startYmd: string, endYmd: string): string {
    return `${startYmd},${endYmd}`;
}

export function parseDayRangeValue(value: string): { start: string; end: string } | null {
    if (!value || value === 'all' || !value.includes(',')) return null;
    const [start, end] = value.split(',');
    if (!start || !end || !YMD_REGEX.test(start) || !YMD_REGEX.test(end)) return null;
    return { start, end };
}

export function defaultDayRange(): string {
    const nowUTC = new Date();
    const nowChilean = convertUTCToChilean(nowUTC);
    const end = new Date(nowChilean);
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    return formatDayRangeValue(formatYmdFromChileanDate(start), formatYmdFromChileanDate(end));
}

export function formatDayRangeLabel(value: string): string {
    const parsed = parseDayRangeValue(value);
    if (!parsed) return 'Select dates';
    const fmt = (ymd: string) => {
        const [y, m, d] = ymd.split('-').map(Number);
        const date = new Date(y!, m! - 1, d);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    const year = parsed.start.slice(0, 4);
    return `${fmt(parsed.start)} – ${fmt(parsed.end)}, ${year}`;
}

/** date-fns locale for review calendars — week starts on Monday. */
export const reviewDateRangeLocale = {
  ...enUS,
  options: {
    ...enUS.options,
    weekStartsOn: 1 as const,
  },
};

export interface ReviewDateOption {
    value: string;
    label: string;
}

const getChileanTimezoneOffset = (date: Date): number => {
    const month = date.getMonth();
    if (month >= 8 || month <= 3) {
        return -4 * 60;
    }
    return -3 * 60;
};

export const convertUTCToChilean = (utcDate: Date): Date => {
    const offsetMinutes = getChileanTimezoneOffset(utcDate);
    return new Date(utcDate.getTime() + offsetMinutes * 60 * 1000);
};

export function buildWeekOptions(): ReviewDateOption[] {
    const weeks: ReviewDateOption[] = [{ value: 'all', label: 'All Weeks' }];

    const nowUTC = new Date();
    const nowChilean = convertUTCToChilean(nowUTC);

    for (let i = 0; i < 12; i++) {
        const weekStart = new Date(nowChilean);
        const daysSinceMonday = (nowChilean.getDay() + 6) % 7;
        weekStart.setDate(nowChilean.getDate() - (daysSinceMonday + i * 7));
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const formatDate = (date: Date) => {
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        };

        const year = weekStart.getFullYear();
        const month = String(weekStart.getMonth() + 1).padStart(2, '0');
        const day = String(weekStart.getDate()).padStart(2, '0');
        const dateValue = `${year}-${month}-${day}`;

        weeks.push({
            value: dateValue,
            label: `${formatDate(weekStart)} - ${formatDate(weekEnd)}, ${year}`,
        });
    }

    return weeks;
}

export function buildMonthOptions(): ReviewDateOption[] {
    const months: ReviewDateOption[] = [{ value: 'all', label: 'All Months' }];

    const nowUTC = new Date();
    const nowChilean = convertUTCToChilean(nowUTC);

    for (let i = 0; i < 12; i++) {
        const monthDate = new Date(nowChilean);
        monthDate.setMonth(nowChilean.getMonth() - i);

        const firstDay = new Date(monthDate);
        firstDay.setDate(1);
        firstDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(monthDate);
        lastDay.setMonth(monthDate.getMonth() + 1);
        lastDay.setDate(0);
        lastDay.setHours(23, 59, 59, 999);

        const year = firstDay.getFullYear();
        const month = String(firstDay.getMonth() + 1).padStart(2, '0');
        const monthName = firstDay.toLocaleDateString('en-US', { month: 'long' });

        const firstDayStr = `${year}-${month}-01`;
        const lastDayNum = lastDay.getDate();
        const lastDayStr = `${year}-${month}-${String(lastDayNum).padStart(2, '0')}`;
        const dateValue = `${firstDayStr},${lastDayStr}`;

        months.push({
            value: dateValue,
            label: `${monthName} ${year}`,
        });
    }

    return months;
}
